import { addMinutes } from 'date-fns';

export const MERGE_ITEMS_LIMIT = 2;
export const COLOR_STATES = {
  default: '#3f3f3f',
  AVAILABLE: '#1bcbdd',
};

/**
 * Just remaps the event structure so that it is understandable by
 * the fullcalendar library. This function has dependencies over
 * live instruction API data model and the fullcalendar specs.
 *
 * @param {Object[]} eventArray a set of events fetched by LI code
 * @returns {Object[]} a list of fullcalendar events
 *                          (https://fullcalendar.io/docs/event-object)
 */
function remapEvents(eventArray) {
  return eventArray.map(item => {
    const color = COLOR_STATES[item.status] || COLOR_STATES.default;
    const start = new Date(item.scheduled_at);

    return {
      title: item.status,
      start,
      end: addMinutes(start, item.duration_minutes),
      eventContent: item.status,
      backgroundColor: color,
    };
  });
}

/**
 * Groups together adjacent events to ease the life of
 * users not having to click on each 15-minute slot
 * @param {Object[]} eventArray a list of fullcalendar events (https://fullcalendar.io/docs/event-object)
 * @returns {Object[]} a list of fullcalendar events
 *                          (https://fullcalendar.io/docs/event-object)
 */
function mergeConsecutiveEvents(eventArray) {
  const newArray = [];
  let previousItem = null;
  let mergedItemsCounter = 0;

  eventArray.forEach(item => {
    if (previousItem == null) {
      previousItem = item;
      return;
    }
    mergedItemsCounter += 1;
    const areEventsAdjacent = item.start.getTime() === previousItem.end.getTime();
    const areEventTypesEqual = item.title === previousItem.title;
    if (areEventsAdjacent && areEventTypesEqual && mergedItemsCounter < MERGE_ITEMS_LIMIT) {
      previousItem.end = item.end;
    } else {
      newArray.push(previousItem);
      previousItem = item;
      mergedItemsCounter = 0;
    }
  });

  if (previousItem != null) {
    newArray.push(previousItem);
  }
  return newArray;
}

/**
 * Performs a set of post-processing activities allowing to
 * convert a set of events fetched from the LI availability API
 * (https://dev.voxy.com/live-instruction/v0/docs#/Calendar/get_availability_by_date_calendar_avail__get)
 * towards a set of events compatible with fullCalendar format
 * (https://fullcalendar.io/docs/event-object).
 *
 * @param {Object[]} rawEvents a set of events fetched from LI endpoint
 * @returns {Object[]} a list of fullcalendar events
 *                          (https://fullcalendar.io/docs/event-object)
 */
export const postProcessEvents = rawEvents => {
  let events = remapEvents(rawEvents);
  if (MERGE_ITEMS_LIMIT > 1) {
    events = mergeConsecutiveEvents(events);
  }
  return events;
};

export default { postProcessEvents, MERGE_ITEMS_LIMIT, COLOR_STATES };
